import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, doc, getDoc, updateDoc, deleteDoc, storage } from '../config/firebase';
import { toast } from 'sonner';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, ImagePlus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { imageService } from '../services/imageService';
import { EVENT_GALLERY_REQUIREMENTS } from '../config/imageRequirements';
import { ImageCropper } from '../components/ui/image-cropper';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

export default function EditEvent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cropImage, setCropImage] = useState<{ file: File; preview: string } | null>(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) {
        toast.error('No event ID provided');
        navigate('/dashboard');
        return;
      }

      try {
        const eventDoc = await getDoc(doc(db, 'events', id));
        if (!eventDoc.exists()) {
          toast.error('Event not found');
          navigate('/dashboard');
          return;
        }

        const data = eventDoc.data();
        // Convert Firestore timestamps to JavaScript Date objects
        if (data.startDateTime) {
          data.startDateTime = data.startDateTime.toDate();
        }
        if (data.endDateTime) {
          data.endDateTime = data.endDateTime.toDate();
        }
        if (data.createdAt) {
          data.createdAt = data.createdAt.toDate();
        }
        if (data.updatedAt) {
          data.updatedAt = data.updatedAt.toDate();
        }

        setEvent({ id: eventDoc.id, ...data });
        setImages(data.media?.images || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Error loading event');
        navigate('/dashboard');
      }
    };

    fetchEvent();
  }, [id, navigate]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setError(null);
      const file = files[0]; // Handle one file at a time for cropping
      
      // Create preview URL for the cropper
      const preview = URL.createObjectURL(file);
      setCropImage({ file, preview });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Invalid image file');
      toast.error('Failed to add image: ' + error);
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      setError(null);
      
      // Convert blob to file
      const file = new File([croppedBlob], cropImage?.file.name || 'cropped-image.jpg', {
        type: 'image/jpeg'
      });

      // Validate the cropped image
      await imageService.validateImage(file, EVENT_GALLERY_REQUIREMENTS);
      
      // Add to newImages array
      setNewImages(prev => [...prev, file]);
      
      // Clean up
      if (cropImage?.preview) {
        URL.revokeObjectURL(cropImage.preview);
      }
      setCropImage(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Invalid image file');
      toast.error('Failed to process image: ' + error);
    }
  };

  const handleImageDelete = (imageUrl: string) => {
    setImages(prev => prev.filter(img => img !== imageUrl));
    setImagesToDelete(prev => [...prev, imageUrl]);
  };

  const handleNewImageDelete = (index: number) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!event || !id) return;
    setSaving(true);
    setError(null);

    try {
      // Upload new images to Firebase Storage
      const uploadedUrls = await Promise.all(
        newImages.map(async (file) => {
          const { url } = await imageService.uploadImage(file, `events/${id}/gallery`);
          return url;
        })
      );

      // Delete removed images from Firebase Storage
      await Promise.all(
        imagesToDelete.map(async (url) => {
          try {
            await imageService.deleteImage(url);
          } catch (error) {
            console.error('Error deleting image:', error);
          }
        })
      );

      // Update event document with new image URLs
      const updatedImages = [...images.filter(img => !imagesToDelete.includes(img)), ...uploadedUrls];
      await updateDoc(doc(db, 'events', id), {
        ...event,
        media: {
          ...event.media,
          images: updatedImages,
        },
        updatedAt: new Date(),
      });

      toast.success('Event updated successfully');
      navigate(`/events/${id}`);
    } catch (error) {
      console.error('Error updating event:', error);
      setError(error instanceof Error ? error.message : 'Failed to update event');
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setError(null);

    try {
      // Delete all images from storage
      const allImages = [...images, ...(event.media?.images || [])];
      await Promise.all(
        allImages.map(async (url) => {
          try {
            await imageService.deleteImage(url);
          } catch (error) {
            console.error('Error deleting image:', error);
          }
        })
      );

      // Delete event document
      await deleteDoc(doc(db, 'events', id));
      toast.success('Event deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting event:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete event');
      toast.error('Failed to delete event');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card>
        <CardHeader>
          <CardTitle>Edit Event</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Event Title</Label>
              <Input
                id="title"
                value={event.title}
                onChange={(e) => setEvent({ ...event, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={event.description}
                onChange={(e) => setEvent({ ...event, description: e.target.value })}
                rows={5}
              />
            </div>

            <div className="space-y-2">
              <Label>Event Images</Label>
              {error && (
                <Alert variant="destructive">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((url, index) => (
                  <div key={url} className="relative group aspect-video">
                    <img
                      src={url}
                      alt={`Event ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleImageDelete(url)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {newImages.map((file, index) => (
                  <div key={index} className="relative group aspect-video">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`New upload ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleNewImageDelete(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <label className="relative group flex flex-col items-center justify-center aspect-video border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <ImagePlus className="h-8 w-8 mb-2 text-gray-400" />
                  <span className="text-sm text-gray-500">Add Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={saving || deleting}
                  />
                </label>
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={saving || deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Event'
                )}
              </Button>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/events/${id}`)}
                  disabled={saving || deleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || deleting}
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {cropImage && (
        <ImageCropper
          image={cropImage.preview}
          aspectRatio={16/9}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            URL.revokeObjectURL(cropImage.preview);
            setCropImage(null);
          }}
        />
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
} 